/**
 * Persona Schema
 * 
 * Personas define how the Intelligence Cockpit presents content to different
 * types of users. Each persona configures tab ordering, block priorities,
 * default expansion states, scenario defaults, and export templates.
 * 
 * @version 2.2
 */

import { z } from 'zod'
import { TabIdSchema } from './views'
import { BlockType, BLOCK_CATEGORIES } from './blocks'

// ============================================================================
// Export Template Schema
// ============================================================================

export const ExportTemplateSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  format: z.enum(['pdf', 'html', 'docx', 'pptx']),
  sections: z.array(z.enum([
    'cover',
    'bluf',
    'executive_summary',
    'key_findings',
    'toc',
    'figures',
    'main_body',
    'decision_options',
    'watchlist',
    'checklist',
    'counterpoints',
    'implications',
    'methodology',
    'limitations',
    'sources',
    'pinned_evidence',
    'timeline',
    'causal_chain',
    'comparison_grid',
    'stakeholder_map',
    'data_tables',
    'glossary',
    'appendix',
  ])),
  max_pages: z.number().optional(),
  style: z.enum(['formal', 'executive', 'technical', 'academic']).optional(),
})
export type ExportTemplate = z.infer<typeof ExportTemplateSchema>

// ============================================================================
// Block Priority Schema
// ============================================================================

export const BlockPrioritySchema = z.record(
  z.string(), // Block type
  z.number().min(0).max(1) // Priority weight (0 = hidden, 1 = highest priority)
)
export type BlockPriority = z.infer<typeof BlockPrioritySchema>

// ============================================================================
// Persona Schema
// ============================================================================

export const PersonaGroupSchema = z.enum([
  'consultants',
  'finance_analysts',
  'vcs',
  'corporate',
  'legal',
  'academia',
  'scientific_labs',
  'students',
  'enthusiasts',
  'general',
])
export type PersonaGroup = z.infer<typeof PersonaGroupSchema>

export const PersonaSchema = z.object({
  persona_id: z.string(),
  label: z.string(),
  description: z.string(),
  group: PersonaGroupSchema,
  
  // Tab configuration
  tab_ordering: z.array(TabIdSchema),
  hidden_tabs: z.array(TabIdSchema).optional(),
  
  // Block configuration
  block_priorities: BlockPrioritySchema.optional(),
  default_collapsed: z.array(z.string()).optional(), // Block types that start collapsed
  default_expanded: z.array(z.string()).optional(), // Block types that start expanded
  
  // Scenario configuration
  default_scenario_axis: z.string().optional(), // If report has multiple axes
  default_scenario_state: z.string().optional(),
  
  // Export configuration
  export_templates: z.array(ExportTemplateSchema),
  default_export_template: z.string().optional(),
  
  // Reading mode configuration
  default_reading_mode: z.enum(['3_minute', '10_minute', 'deep_dive']).optional(),
  
  // Module preferences (which blocks to emphasize)
  emphasized_modules: z.array(z.string()).optional(), // Block type IDs
  de_emphasized_modules: z.array(z.string()).optional(),
  
  // Visual preferences
  dense_layout: z.boolean().optional(),
  show_confidence_indicators: z.boolean().optional(),
  show_evidence_counts: z.boolean().optional(),
})
export type Persona = z.infer<typeof PersonaSchema>

// ============================================================================
// Persona Library Schema
// ============================================================================

export const PersonaLibrarySchema = z.object({
  schema_version: z.literal('2.2'),
  personas: z.array(PersonaSchema),
  groups: z.array(z.object({
    id: PersonaGroupSchema,
    label: z.string(),
    description: z.string().optional(),
    persona_ids: z.array(z.string()),
  })),
})
export type PersonaLibrary = z.infer<typeof PersonaLibrarySchema>

// ============================================================================
// Default Personas (Top 30)
// ============================================================================

export const DEFAULT_PERSONAS: Persona[] = [
  // CONSULTANTS
  {
    persona_id: 'strategy_consultant',
    label: 'Strategy Consultant',
    description: 'Senior consultants focused on strategic recommendations and options analysis',
    group: 'consultants',
    tab_ordering: ['cockpit', 'takeaways', 'written_report', 'data_figures', 'connections', 'sources_methods'],
    block_priorities: {
      decision_options: 1.0,
      comparison_grid: 0.95,
      causal_chain: 0.9,
      watchlist: 0.85,
      verdict: 0.8,
    },
    default_collapsed: ['glossary', 'change_log'],
    export_templates: [
      {
        id: 'exec_memo',
        label: 'Executive Memo',
        format: 'pdf',
        sections: ['cover', 'bluf', 'executive_summary', 'decision_options', 'key_findings', 'watchlist', 'sources'],
        max_pages: 3,
        style: 'executive',
      },
      {
        id: 'options_table',
        label: 'Options Analysis',
        format: 'pdf',
        sections: ['cover', 'bluf', 'decision_options', 'counterpoints', 'sources'],
        style: 'formal',
      },
    ],
    default_export_template: 'exec_memo',
    emphasized_modules: ['decision_options', 'causal_chain', 'watchlist'],
    dense_layout: true,
  },
  {
    persona_id: 'ops_consultant',
    label: 'Operations Consultant',
    description: 'Transformation and operations consultants focused on implementation',
    group: 'consultants',
    tab_ordering: ['cockpit', 'takeaways', 'data_figures', 'written_report', 'sources_methods', 'connections'],
    block_priorities: {
      checklist: 1.0,
      timeline: 0.95,
      watchlist: 0.9,
      data_table: 0.85,
    },
    export_templates: [
      {
        id: '30_60_90',
        label: '30-60-90 Plan',
        format: 'pdf',
        sections: ['cover', 'bluf', 'checklist', 'timeline', 'watchlist', 'sources'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['checklist', 'timeline', 'data_table'],
  },
  
  // FINANCE ANALYSTS
  {
    persona_id: 'equity_analyst',
    label: 'Public Markets Equity Analyst',
    description: 'Buy-side/sell-side analysts covering public equities',
    group: 'finance_analysts',
    tab_ordering: ['cockpit', 'data_figures', 'takeaways', 'written_report', 'sources_methods', 'connections'],
    block_priorities: {
      viz_frame: 1.0,
      data_table: 0.95,
      comparison_grid: 0.9,
      timeline: 0.85,
      signal_card: 0.8,
    },
    default_scenario_state: 'base_case',
    export_templates: [
      {
        id: 'research_note',
        label: 'Research Note',
        format: 'pdf',
        sections: ['cover', 'bluf', 'executive_summary', 'key_findings', 'figures', 'main_body', 'sources'],
        style: 'formal',
      },
      {
        id: 'model_appendix',
        label: 'Model Appendix',
        format: 'pdf',
        sections: ['figures', 'methodology', 'sources'],
        style: 'technical',
      },
    ],
    emphasized_modules: ['viz_frame', 'data_table', 'comparison_grid'],
    show_confidence_indicators: true,
  },
  {
    persona_id: 'credit_analyst',
    label: 'Credit Analyst',
    description: 'Fixed income and credit analysts focused on debt instruments',
    group: 'finance_analysts',
    tab_ordering: ['cockpit', 'data_figures', 'takeaways', 'sources_methods', 'written_report', 'connections'],
    block_priorities: {
      data_table: 1.0,
      watchlist: 0.95,
      signal_card: 0.9,
    },
    export_templates: [
      {
        id: 'credit_memo',
        label: 'Credit Memo',
        format: 'pdf',
        sections: ['cover', 'bluf', 'key_findings', 'figures', 'watchlist', 'sources'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['data_table', 'watchlist', 'signal_card'],
  },
  {
    persona_id: 'macro_strategist',
    label: 'Macro/FX Strategist',
    description: 'Macro economists and FX strategists',
    group: 'finance_analysts',
    tab_ordering: ['cockpit', 'data_figures', 'connections', 'takeaways', 'written_report', 'sources_methods'],
    block_priorities: {
      viz_frame: 1.0,
      causal_chain: 0.95,
      timeline: 0.9,
    },
    export_templates: [
      {
        id: 'macro_brief',
        label: 'Macro Brief',
        format: 'pdf',
        sections: ['cover', 'bluf', 'figures', 'key_findings', 'sources'],
        style: 'executive',
        max_pages: 5,
      },
    ],
    emphasized_modules: ['viz_frame', 'causal_chain', 'timeline'],
  },
  {
    persona_id: 'hf_analyst',
    label: 'Hedge Fund Analyst',
    description: 'Analysts at hedge funds focused on variant perception',
    group: 'finance_analysts',
    tab_ordering: ['cockpit', 'takeaways', 'data_figures', 'sources_methods', 'written_report', 'connections'],
    block_priorities: {
      verdict: 1.0,
      claim_card: 0.95,
      timeline: 0.9,
      watchlist: 0.85,
    },
    export_templates: [
      {
        id: 'variant_memo',
        label: 'Variant Perception Memo',
        format: 'pdf',
        sections: ['cover', 'bluf', 'counterpoints', 'key_findings', 'watchlist', 'sources'],
        style: 'executive',
      },
    ],
    emphasized_modules: ['verdict', 'claim_card', 'watchlist'],
  },
  
  // VCs
  {
    persona_id: 'vc_partner',
    label: 'VC Partner',
    description: 'Senior partners at venture capital firms',
    group: 'vcs',
    tab_ordering: ['cockpit', 'takeaways', 'connections', 'data_figures', 'written_report', 'sources_methods'],
    block_priorities: {
      verdict: 1.0,
      comparison_grid: 0.95,
      decision_options: 0.9,
      causal_chain: 0.85,
    },
    export_templates: [
      {
        id: 'ic_memo',
        label: 'IC Memo',
        format: 'pdf',
        sections: ['cover', 'bluf', 'executive_summary', 'decision_options', 'key_findings', 'counterpoints', 'sources'],
        style: 'executive',
        max_pages: 5,
      },
    ],
    emphasized_modules: ['verdict', 'comparison_grid', 'decision_options'],
    dense_layout: true,
  },
  {
    persona_id: 'vc_associate',
    label: 'VC Associate',
    description: 'Associates and principals doing diligence',
    group: 'vcs',
    tab_ordering: ['cockpit', 'data_figures', 'takeaways', 'connections', 'sources_methods', 'written_report'],
    block_priorities: {
      comparison_grid: 1.0,
      checklist: 0.95,
      data_table: 0.9,
      entity_profile: 0.85,
    },
    export_templates: [
      {
        id: 'diligence_packet',
        label: 'Diligence Packet',
        format: 'pdf',
        sections: ['cover', 'executive_summary', 'figures', 'checklist', 'sources', 'pinned_evidence'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['comparison_grid', 'checklist', 'data_table'],
  },
  
  // CORPORATE
  {
    persona_id: 'pe_corp_dev',
    label: 'Private Equity / Corp Dev',
    description: 'PE professionals and corporate development teams',
    group: 'corporate',
    tab_ordering: ['cockpit', 'data_figures', 'takeaways', 'written_report', 'sources_methods', 'connections'],
    block_priorities: {
      comparison_grid: 1.0,
      timeline: 0.95,
      data_table: 0.9,
      checklist: 0.85,
    },
    export_templates: [
      {
        id: 'deal_brief',
        label: 'Deal Brief',
        format: 'pdf',
        sections: ['cover', 'bluf', 'figures', 'key_findings', 'decision_options', 'sources'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['comparison_grid', 'timeline', 'data_table'],
  },
  {
    persona_id: 'corp_strategy',
    label: 'Corporate Strategy',
    description: 'In-house strategy teams at corporations',
    group: 'corporate',
    tab_ordering: ['cockpit', 'takeaways', 'written_report', 'data_figures', 'connections', 'sources_methods'],
    block_priorities: {
      decision_options: 1.0,
      comparison_grid: 0.95,
      causal_chain: 0.9,
    },
    export_templates: [
      {
        id: 'strategy_memo',
        label: 'Strategy Memo',
        format: 'pdf',
        sections: ['cover', 'bluf', 'decision_options', 'key_findings', 'implications', 'sources'],
        style: 'executive',
      },
    ],
    emphasized_modules: ['decision_options', 'comparison_grid'],
  },
  {
    persona_id: 'product_manager',
    label: 'Product Manager',
    description: 'Product managers researching market/technical topics',
    group: 'corporate',
    tab_ordering: ['cockpit', 'takeaways', 'data_figures', 'written_report', 'connections', 'sources_methods'],
    block_priorities: {
      checklist: 1.0,
      decision_options: 0.95,
      comparison_grid: 0.9,
    },
    export_templates: [
      {
        id: 'prd_summary',
        label: 'PRD Summary',
        format: 'pdf',
        sections: ['cover', 'bluf', 'key_findings', 'decision_options', 'checklist', 'sources'],
        style: 'technical',
      },
    ],
    emphasized_modules: ['checklist', 'decision_options'],
  },
  {
    persona_id: 'engineering_lead',
    label: 'Engineering Lead',
    description: 'Technical leads evaluating architectures and technologies',
    group: 'corporate',
    tab_ordering: ['cockpit', 'data_figures', 'takeaways', 'written_report', 'sources_methods', 'connections'],
    block_priorities: {
      viz_frame: 1.0,
      comparison_grid: 0.95,
      checklist: 0.9,
    },
    export_templates: [
      {
        id: 'design_doc',
        label: 'Design Doc Outline',
        format: 'pdf',
        sections: ['cover', 'bluf', 'figures', 'decision_options', 'checklist', 'sources'],
        style: 'technical',
      },
    ],
    emphasized_modules: ['viz_frame', 'comparison_grid'],
  },
  {
    persona_id: 'general_exec',
    label: 'General Executive',
    description: 'C-suite and senior executives needing quick briefs',
    group: 'corporate',
    tab_ordering: ['cockpit', 'takeaways', 'written_report', 'data_figures', 'connections', 'sources_methods'],
    block_priorities: {
      status_strip: 1.0,
      verdict: 0.95,
      signal_card: 0.9,
      watchlist: 0.85,
    },
    default_reading_mode: '3_minute',
    export_templates: [
      {
        id: 'one_pager',
        label: '1-Page Memo',
        format: 'pdf',
        sections: ['bluf', 'key_findings', 'watchlist'],
        max_pages: 1,
        style: 'executive',
      },
    ],
    emphasized_modules: ['verdict', 'signal_card', 'watchlist'],
    dense_layout: true,
  },
  
  // LEGAL
  {
    persona_id: 'litigator',
    label: 'Litigator',
    description: 'Trial lawyers and litigation support',
    group: 'legal',
    tab_ordering: ['cockpit', 'written_report', 'sources_methods', 'takeaways', 'connections', 'data_figures'],
    block_priorities: {
      issue_tree: 1.0,
      timeline: 0.95,
      evidence_bundle: 0.9,
      entity_profile: 0.85,
    },
    export_templates: [
      {
        id: 'legal_memo',
        label: 'Legal Memo',
        format: 'pdf',
        sections: ['cover', 'bluf', 'main_body', 'sources', 'pinned_evidence'],
        style: 'formal',
      },
      {
        id: 'authority_appendix',
        label: 'Authority Appendix',
        format: 'pdf',
        sections: ['sources', 'pinned_evidence'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['issue_tree', 'timeline', 'evidence_bundle'],
  },
  {
    persona_id: 'in_house_counsel',
    label: 'In-House Counsel',
    description: 'Corporate lawyers providing practical guidance',
    group: 'legal',
    tab_ordering: ['cockpit', 'takeaways', 'written_report', 'sources_methods', 'data_figures', 'connections'],
    block_priorities: {
      checklist: 1.0,
      decision_options: 0.95,
      watchlist: 0.9,
    },
    export_templates: [
      {
        id: 'counsel_note',
        label: 'Counsel Note',
        format: 'pdf',
        sections: ['cover', 'bluf', 'key_findings', 'decision_options', 'checklist', 'sources'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['checklist', 'decision_options'],
  },
  {
    persona_id: 'policy_analyst',
    label: 'Policy Analyst',
    description: 'Think tank and government policy researchers',
    group: 'legal',
    tab_ordering: ['cockpit', 'written_report', 'takeaways', 'connections', 'data_figures', 'sources_methods'],
    block_priorities: {
      decision_options: 1.0,
      comparison_grid: 0.95,
      timeline: 0.9,
    },
    export_templates: [
      {
        id: 'policy_memo',
        label: 'Policy Memo',
        format: 'pdf',
        sections: ['cover', 'bluf', 'executive_summary', 'decision_options', 'implications', 'sources'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['decision_options', 'comparison_grid'],
  },
  {
    persona_id: 'compliance_officer',
    label: 'Compliance Officer',
    description: 'Compliance and regulatory professionals',
    group: 'legal',
    tab_ordering: ['cockpit', 'takeaways', 'sources_methods', 'written_report', 'data_figures', 'connections'],
    block_priorities: {
      checklist: 1.0,
      evidence_bundle: 0.95,
      watchlist: 0.9,
    },
    export_templates: [
      {
        id: 'compliance_packet',
        label: 'Compliance Packet',
        format: 'pdf',
        sections: ['cover', 'executive_summary', 'checklist', 'sources', 'pinned_evidence'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['checklist', 'evidence_bundle'],
  },
  {
    persona_id: 'security_researcher',
    label: 'Security / Threat Intel',
    description: 'Cybersecurity researchers and threat analysts',
    group: 'legal',
    tab_ordering: ['cockpit', 'data_figures', 'takeaways', 'connections', 'sources_methods', 'written_report'],
    block_priorities: {
      timeline: 1.0,
      data_table: 0.95,
      checklist: 0.9,
      causal_chain: 0.85,
    },
    export_templates: [
      {
        id: 'threat_brief',
        label: 'Threat Brief',
        format: 'pdf',
        sections: ['cover', 'bluf', 'figures', 'checklist', 'sources'],
        style: 'technical',
      },
    ],
    emphasized_modules: ['timeline', 'data_table', 'checklist'],
  },
  
  // ACADEMIA
  {
    persona_id: 'academic_researcher',
    label: 'Academic Researcher',
    description: 'Professors and research staff at universities',
    group: 'academia',
    tab_ordering: ['written_report', 'sources_methods', 'connections', 'data_figures', 'cockpit', 'takeaways'],
    block_priorities: {
      evidence_bundle: 1.0,
      limitations: 0.95,
      glossary: 0.9,
    },
    export_templates: [
      {
        id: 'annotated_bib',
        label: 'Annotated Bibliography',
        format: 'pdf',
        sections: ['sources', 'methodology', 'limitations'],
        style: 'academic',
      },
    ],
    emphasized_modules: ['evidence_bundle', 'limitations'],
    show_confidence_indicators: true,
    show_evidence_counts: true,
  },
  
  // SCIENTIFIC LABS
  {
    persona_id: 'lab_pi',
    label: 'Scientific Lab PI',
    description: 'Principal investigators running research labs',
    group: 'scientific_labs',
    tab_ordering: ['cockpit', 'data_figures', 'written_report', 'sources_methods', 'connections', 'takeaways'],
    block_priorities: {
      viz_frame: 1.0,
      data_table: 0.95,
      checklist: 0.9,
    },
    export_templates: [
      {
        id: 'lab_briefing',
        label: 'Lab Briefing',
        format: 'pdf',
        sections: ['cover', 'bluf', 'figures', 'key_findings', 'methodology', 'sources'],
        style: 'technical',
      },
    ],
    emphasized_modules: ['viz_frame', 'data_table'],
  },
  {
    persona_id: 'bench_scientist',
    label: 'Bench Scientist',
    description: 'Lab scientists focused on methods and data',
    group: 'scientific_labs',
    tab_ordering: ['data_figures', 'sources_methods', 'written_report', 'cockpit', 'connections', 'takeaways'],
    block_priorities: {
      data_table: 1.0,
      viz_frame: 0.95,
      evidence_bundle: 0.9,
    },
    export_templates: [
      {
        id: 'exp_plan',
        label: 'Experimental Plan Notes',
        format: 'pdf',
        sections: ['figures', 'methodology', 'sources'],
        style: 'technical',
      },
    ],
    emphasized_modules: ['data_table', 'viz_frame'],
  },
  {
    persona_id: 'biostatistician',
    label: 'Biostatistician / Data Scientist',
    description: 'Statistical and data scientists in research',
    group: 'scientific_labs',
    tab_ordering: ['data_figures', 'sources_methods', 'cockpit', 'written_report', 'takeaways', 'connections'],
    block_priorities: {
      data_table: 1.0,
      viz_frame: 0.95,
      assumptions: 0.9,
      limitations: 0.85,
    },
    export_templates: [
      {
        id: 'methods_appendix',
        label: 'Methods Appendix',
        format: 'pdf',
        sections: ['methodology', 'figures', 'limitations', 'sources'],
        style: 'technical',
      },
    ],
    emphasized_modules: ['data_table', 'assumptions', 'limitations'],
  },
  {
    persona_id: 'clinical_research',
    label: 'Clinical Research / Medical Affairs',
    description: 'Clinical researchers and medical affairs professionals',
    group: 'scientific_labs',
    tab_ordering: ['cockpit', 'data_figures', 'takeaways', 'written_report', 'sources_methods', 'connections'],
    block_priorities: {
      timeline: 1.0,
      data_table: 0.95,
      watchlist: 0.9,
    },
    export_templates: [
      {
        id: 'clinical_summary',
        label: 'Clinical Summary',
        format: 'pdf',
        sections: ['cover', 'bluf', 'figures', 'key_findings', 'watchlist', 'sources'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['timeline', 'data_table'],
  },
  
  // STUDENTS
  {
    persona_id: 'grad_student',
    label: 'Graduate Student',
    description: 'Masters and PhD students doing research',
    group: 'students',
    tab_ordering: ['written_report', 'sources_methods', 'data_figures', 'connections', 'cockpit', 'takeaways'],
    block_priorities: {
      evidence_bundle: 1.0,
      glossary: 0.95,
      limitations: 0.9,
    },
    export_templates: [
      {
        id: 'research_notes',
        label: 'Research Notes',
        format: 'pdf',
        sections: ['executive_summary', 'key_findings', 'sources', 'methodology'],
        style: 'academic',
      },
    ],
    emphasized_modules: ['evidence_bundle', 'glossary'],
  },
  {
    persona_id: 'undergrad_student',
    label: 'Undergraduate Student',
    description: 'Undergraduate students learning topics',
    group: 'students',
    tab_ordering: ['cockpit', 'written_report', 'data_figures', 'takeaways', 'connections', 'sources_methods'],
    block_priorities: {
      glossary: 1.0,
      viz_frame: 0.95,
      verdict: 0.9,
    },
    default_reading_mode: '10_minute',
    export_templates: [
      {
        id: 'study_guide',
        label: 'Study Guide',
        format: 'pdf',
        sections: ['bluf', 'executive_summary', 'key_findings', 'figures'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['glossary', 'viz_frame', 'verdict'],
  },
  
  // ENTHUSIASTS
  {
    persona_id: 'tech_enthusiast',
    label: 'Tech Enthusiast',
    description: 'Technology enthusiasts following industry trends',
    group: 'enthusiasts',
    tab_ordering: ['cockpit', 'written_report', 'data_figures', 'takeaways', 'connections', 'sources_methods'],
    block_priorities: {
      viz_frame: 1.0,
      timeline: 0.95,
      verdict: 0.9,
    },
    export_templates: [
      {
        id: 'reading_list',
        label: 'Personal Reading List',
        format: 'pdf',
        sections: ['bluf', 'key_findings', 'sources'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['viz_frame', 'timeline'],
  },
  {
    persona_id: 'history_geopolitics',
    label: 'History / Geopolitics Enthusiast',
    description: 'History buffs and geopolitics followers',
    group: 'enthusiasts',
    tab_ordering: ['written_report', 'cockpit', 'connections', 'data_figures', 'takeaways', 'sources_methods'],
    block_priorities: {
      timeline: 1.0,
      entity_profile: 0.95,
      viz_frame: 0.9,
    },
    export_templates: [
      {
        id: 'annotated_timeline',
        label: 'Annotated Timeline',
        format: 'pdf',
        sections: ['bluf', 'figures', 'sources'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['timeline', 'entity_profile'],
  },
  {
    persona_id: 'journalist',
    label: 'Journalist / Writer',
    description: 'Journalists and writers researching stories',
    group: 'enthusiasts',
    tab_ordering: ['cockpit', 'written_report', 'connections', 'sources_methods', 'takeaways', 'data_figures'],
    block_priorities: {
      timeline: 1.0,
      entity_profile: 0.95,
      evidence_bundle: 0.9,
    },
    export_templates: [
      {
        id: 'story_brief',
        label: 'Story Brief',
        format: 'pdf',
        sections: ['bluf', 'key_findings', 'sources'],
        style: 'formal',
      },
      {
        id: 'source_pack',
        label: 'Source Pack',
        format: 'pdf',
        sections: ['sources', 'pinned_evidence'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['timeline', 'entity_profile', 'evidence_bundle'],
  },
  {
    persona_id: 'educator',
    label: 'Educator / Lecturer',
    description: 'Teachers and professors preparing lessons',
    group: 'enthusiasts',
    tab_ordering: ['written_report', 'cockpit', 'data_figures', 'takeaways', 'connections', 'sources_methods'],
    block_priorities: {
      glossary: 1.0,
      viz_frame: 0.95,
      verdict: 0.9,
    },
    export_templates: [
      {
        id: 'lesson_plan',
        label: 'Lesson Plan',
        format: 'pdf',
        sections: ['bluf', 'executive_summary', 'figures', 'key_findings'],
        style: 'formal',
      },
    ],
    emphasized_modules: ['glossary', 'viz_frame'],
  },
  
  // GENERAL
  {
    persona_id: 'founder_operator',
    label: 'Founder / Operator',
    description: 'Startup founders and business operators',
    group: 'general',
    tab_ordering: ['cockpit', 'takeaways', 'data_figures', 'written_report', 'connections', 'sources_methods'],
    block_priorities: {
      decision_options: 1.0,
      checklist: 0.95,
      watchlist: 0.9,
    },
    export_templates: [
      {
        id: 'action_plan',
        label: 'Action Plan',
        format: 'pdf',
        sections: ['bluf', 'decision_options', 'checklist', 'watchlist'],
        style: 'executive',
      },
    ],
    emphasized_modules: ['decision_options', 'checklist'],
    dense_layout: true,
  },
]

// ============================================================================
// Default Persona
// ============================================================================

export const DEFAULT_PERSONA: Persona = {
  persona_id: 'default',
  label: 'Default Reader',
  description: 'Balanced view for general readers',
  group: 'general',
  tab_ordering: ['cockpit', 'written_report', 'takeaways', 'data_figures', 'connections', 'sources_methods'],
  block_priorities: {},
  export_templates: [
    {
      id: 'full_report',
      label: 'Full Report',
      format: 'pdf',
      sections: ['cover', 'toc', 'bluf', 'executive_summary', 'figures', 'main_body', 'sources'],
      style: 'formal',
    },
  ],
  default_export_template: 'full_report',
}



